/**
 * Template field schemas — defines what data fields each product template expects.
 * Used by:
 *   - AI prompt generation (builds JSON schema for LLM)
 *   - Agent tools (stores template-specific data in product_attribute_data)
 *   - Frontend ProductEditor (renders dynamic form fields)
 *   - Migration seeding (attribute groups & attributes)
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type FieldType = "text" | "number" | "select" | "multi_select" | "boolean" | "textarea";

export interface TemplateFieldDef {
  key: string;
  label: string;
  type: FieldType;
  /** For select / multi_select fields */
  options?: string[];
  required?: boolean;
  placeholder?: string;
  /** Display unit (e.g., "inches", "watts") */
  unit?: string;
  /** Logical group for form section headings */
  group: string;
  /** Hint for AI prompt — describes what to generate */
  aiHint?: string;
}

export interface TemplateSchema {
  slug: string;
  name: string;
  baseLayout: BaseLayout;
  /** Combined fields: base_layout fields + template-specific extras */
  fields: TemplateFieldDef[];
  /** Extra AI prompt hints specific to this template */
  aiPromptContext?: string;
}

export type BaseLayout =
  | "seed_plant"
  | "tech_specs"
  | "fashion"
  | "consumable"
  | "vehicle_parts"
  | "media"
  | "service"
  | "generic";

// ---------------------------------------------------------------------------
// Base layout field definitions (shared by all templates using that layout)
// ---------------------------------------------------------------------------

export const BASE_LAYOUT_FIELDS: Record<BaseLayout, TemplateFieldDef[]> = {
  seed_plant: [
    { key: "plant_lifecycle", label: "Plant Lifecycle", type: "select", options: ["annual", "biennial", "perennial"], group: "Plant Characteristics", aiHint: "one of: annual, biennial, perennial" },
    { key: "sun_requirement", label: "Sun Requirement", type: "select", options: ["full_sun", "partial_sun", "partial_shade", "full_shade"], group: "Plant Characteristics", aiHint: "one of: full_sun, partial_sun, partial_shade, full_shade" },
    { key: "water_requirement", label: "Water Requirement", type: "select", options: ["low", "moderate", "high"], group: "Plant Characteristics", aiHint: "one of: low, moderate, high" },
    { key: "difficulty_level", label: "Difficulty Level", type: "select", options: ["beginner", "intermediate", "advanced"], group: "Plant Characteristics", aiHint: "one of: beginner, intermediate, advanced" },
    { key: "zone_min", label: "Zone Min", type: "number", group: "Hardiness Zones", aiHint: "USDA hardiness zone minimum (1-13)" },
    { key: "zone_max", label: "Zone Max", type: "number", group: "Hardiness Zones", aiHint: "USDA hardiness zone maximum (1-13)" },
    { key: "days_to_germination_min", label: "Days to Germination (Min)", type: "number", group: "Growing Timeline", aiHint: "minimum days to germination" },
    { key: "days_to_germination_max", label: "Days to Germination (Max)", type: "number", group: "Growing Timeline", aiHint: "maximum days to germination" },
    { key: "days_to_maturity_min", label: "Days to Maturity (Min)", type: "number", group: "Growing Timeline", aiHint: "minimum days to maturity" },
    { key: "days_to_maturity_max", label: "Days to Maturity (Max)", type: "number", group: "Growing Timeline", aiHint: "maximum days to maturity" },
    { key: "planting_depth", label: "Planting Depth", type: "text", group: "Planting Specs", aiHint: "e.g. '1/4 inch'" },
    { key: "plant_spacing", label: "Plant Spacing", type: "text", group: "Planting Specs", aiHint: "e.g. '18 inches'" },
    { key: "row_spacing", label: "Row Spacing", type: "text", group: "Planting Specs", aiHint: "e.g. '24 inches'" },
    { key: "height_min", label: "Height Min", type: "number", unit: "inches", group: "Planting Specs", aiHint: "mature height minimum in inches" },
    { key: "height_max", label: "Height Max", type: "number", unit: "inches", group: "Planting Specs", aiHint: "mature height maximum in inches" },
    { key: "planting_seasons", label: "Planting Seasons", type: "multi_select", options: ["spring", "summer", "fall", "winter"], group: "Seasons", aiHint: "array of lowercase seasons" },
    { key: "harvest_seasons", label: "Harvest Seasons", type: "multi_select", options: ["spring", "summer", "fall", "winter"], group: "Seasons", aiHint: "array of lowercase seasons" },
  ],

  tech_specs: [
    { key: "brand", label: "Brand", type: "text", group: "General", aiHint: "manufacturer or brand name" },
    { key: "model_number", label: "Model Number", type: "text", group: "General", aiHint: "manufacturer model number or ID" },
    { key: "dimensions", label: "Dimensions", type: "text", group: "Physical", aiHint: "L x W x H with units, e.g. '10 x 5 x 3 inches'" },
    { key: "item_weight", label: "Weight", type: "text", group: "Physical", aiHint: "weight with unit, e.g. '2.5 lbs'" },
    { key: "power_source", label: "Power Source", type: "select", options: ["battery", "ac_power", "usb", "solar", "manual", "gas", "cordless"], group: "Power", aiHint: "one of: battery, ac_power, usb, solar, manual, gas, cordless" },
    { key: "voltage", label: "Voltage", type: "text", unit: "V", group: "Power", aiHint: "voltage rating, e.g. '120V'" },
    { key: "wattage", label: "Wattage", type: "number", unit: "W", group: "Power", aiHint: "power consumption in watts" },
    { key: "warranty_months", label: "Warranty", type: "number", unit: "months", group: "General", aiHint: "warranty duration in months" },
    { key: "certifications", label: "Certifications", type: "multi_select", options: ["UL", "CE", "FCC", "RoHS", "Energy Star", "ETL", "CSA"], group: "Compliance", aiHint: "array of certification names" },
    { key: "connectivity", label: "Connectivity", type: "multi_select", options: ["WiFi", "Bluetooth", "USB-C", "USB-A", "HDMI", "Ethernet", "NFC", "Thunderbolt"], group: "Connectivity", aiHint: "array of connectivity options" },
  ],

  fashion: [
    { key: "material", label: "Material", type: "text", group: "Material & Care", aiHint: "primary material composition, e.g. '100% Cotton' or '80% Polyester, 20% Spandex'" },
    { key: "care_instructions", label: "Care Instructions", type: "textarea", group: "Material & Care", aiHint: "washing and care instructions" },
    { key: "country_of_origin", label: "Country of Origin", type: "text", group: "Details", aiHint: "manufacturing country" },
    { key: "size_type", label: "Size Type", type: "select", options: ["regular", "petite", "plus", "tall", "one_size"], group: "Sizing", aiHint: "one of: regular, petite, plus, tall, one_size" },
    { key: "fit_type", label: "Fit Type", type: "select", options: ["slim", "regular", "relaxed", "oversized", "tailored"], group: "Sizing", aiHint: "one of: slim, regular, relaxed, oversized, tailored" },
    { key: "gender", label: "Gender", type: "select", options: ["men", "women", "unisex", "boys", "girls", "kids"], group: "Details", aiHint: "target gender" },
    { key: "closure_type", label: "Closure Type", type: "select", options: ["zipper", "button", "snap", "hook", "pull_on", "lace_up", "velcro", "buckle", "none"], group: "Details", aiHint: "type of closure" },
    { key: "pattern", label: "Pattern", type: "select", options: ["solid", "striped", "plaid", "floral", "geometric", "animal_print", "abstract", "camo"], group: "Details", aiHint: "pattern type" },
  ],

  consumable: [
    { key: "ingredients", label: "Ingredients", type: "textarea", group: "Ingredients & Nutrition", aiHint: "full ingredients list" },
    { key: "serving_size", label: "Serving Size", type: "text", group: "Ingredients & Nutrition", aiHint: "e.g. '1 capsule', '2 tablespoons', '30g'" },
    { key: "servings_per_container", label: "Servings per Container", type: "number", group: "Ingredients & Nutrition", aiHint: "number of servings" },
    { key: "shelf_life", label: "Shelf Life", type: "text", group: "Storage", aiHint: "e.g. '24 months', '6 months after opening'" },
    { key: "storage_instructions", label: "Storage Instructions", type: "text", group: "Storage", aiHint: "e.g. 'Store in a cool, dry place'" },
    { key: "allergens", label: "Allergens", type: "multi_select", options: ["milk", "eggs", "peanuts", "tree_nuts", "soy", "wheat", "fish", "shellfish", "sesame", "none"], group: "Safety", aiHint: "array of allergen names, or ['none']" },
    { key: "dietary_flags", label: "Dietary Flags", type: "multi_select", options: ["organic", "non_gmo", "gluten_free", "vegan", "vegetarian", "kosher", "halal", "sugar_free", "keto", "paleo"], group: "Dietary", aiHint: "array of applicable dietary certifications" },
  ],

  vehicle_parts: [
    { key: "oem_part_number", label: "OEM Part Number", type: "text", group: "Fitment", aiHint: "original equipment manufacturer part number" },
    { key: "fitment_years", label: "Fitment Years", type: "text", group: "Fitment", aiHint: "compatible model years, e.g. '2018-2024'" },
    { key: "fitment_makes", label: "Fitment Makes", type: "text", group: "Fitment", aiHint: "compatible vehicle makes, comma-separated" },
    { key: "fitment_models", label: "Fitment Models", type: "text", group: "Fitment", aiHint: "compatible vehicle models, comma-separated" },
    { key: "position", label: "Position", type: "select", options: ["front", "rear", "left", "right", "front_left", "front_right", "rear_left", "rear_right", "upper", "lower", "center"], group: "Fitment", aiHint: "installation position on vehicle" },
    { key: "part_material", label: "Material", type: "text", group: "Specs", aiHint: "construction material, e.g. 'Steel', 'Aluminum', 'ABS Plastic'" },
    { key: "warranty_months", label: "Warranty", type: "number", unit: "months", group: "Specs", aiHint: "warranty duration in months" },
    { key: "condition", label: "Condition", type: "select", options: ["new", "remanufactured", "refurbished", "used"], group: "Specs", aiHint: "part condition" },
  ],

  media: [
    { key: "author_artist", label: "Author / Artist", type: "text", group: "Details", aiHint: "creator name(s)" },
    { key: "publisher_label", label: "Publisher / Label", type: "text", group: "Details", aiHint: "publisher or record label" },
    { key: "isbn_upc", label: "ISBN / UPC", type: "text", group: "Details", aiHint: "ISBN for books, UPC for other media" },
    { key: "format", label: "Format", type: "select", options: ["hardcover", "paperback", "ebook", "audiobook", "cd", "vinyl", "digital_download", "streaming", "blu_ray", "dvd"], group: "Format", aiHint: "media format" },
    { key: "release_date", label: "Release Date", type: "text", group: "Details", aiHint: "release or publication date, YYYY-MM-DD" },
    { key: "language", label: "Language", type: "text", group: "Details", aiHint: "primary language" },
    { key: "page_count_runtime", label: "Page Count / Runtime", type: "text", group: "Details", aiHint: "page count for books, runtime for audio/video (e.g. '2h 15m')" },
    { key: "genre", label: "Genre", type: "text", group: "Details", aiHint: "genre or category" },
    { key: "content_rating", label: "Content Rating", type: "select", options: ["G", "PG", "PG-13", "R", "NC-17", "E", "E10+", "T", "M", "not_rated"], group: "Details", aiHint: "content or age rating" },
  ],

  service: [
    { key: "duration_minutes", label: "Duration", type: "number", unit: "minutes", group: "Scheduling", aiHint: "service duration in minutes" },
    { key: "availability", label: "Availability", type: "text", group: "Scheduling", aiHint: "e.g. 'Mon-Fri 9am-5pm', 'By appointment'" },
    { key: "booking_required", label: "Booking Required", type: "boolean", group: "Scheduling", aiHint: "true or false" },
    { key: "provider_name", label: "Provider", type: "text", group: "Provider", aiHint: "service provider name or company" },
    { key: "service_location", label: "Location", type: "select", options: ["in_person", "online", "on_site", "hybrid"], group: "Provider", aiHint: "where service is delivered" },
    { key: "service_area", label: "Service Area", type: "text", group: "Provider", aiHint: "geographic coverage area" },
    { key: "cancellation_policy", label: "Cancellation Policy", type: "text", group: "Policies", aiHint: "cancellation terms" },
  ],

  generic: [
    { key: "dimensions", label: "Dimensions", type: "text", group: "Specifications", aiHint: "L x W x H with units" },
    { key: "item_weight", label: "Weight", type: "text", group: "Specifications", aiHint: "weight with unit" },
    { key: "material", label: "Material", type: "text", group: "Specifications", aiHint: "primary material" },
    { key: "color", label: "Color", type: "text", group: "Specifications", aiHint: "primary color" },
  ],
};

// ---------------------------------------------------------------------------
// Template-specific extra fields (on top of base layout fields)
// ---------------------------------------------------------------------------

const TEMPLATE_EXTRAS: Record<string, { extraFields: TemplateFieldDef[]; aiPromptContext?: string }> = {
  "seeds-plants": {
    extraFields: [],
    aiPromptContext: "This is a seed/plant product. Focus on growing information, hardiness zones, and planting instructions.",
  },

  "electronics": {
    extraFields: [
      { key: "processor", label: "Processor", type: "text", group: "Specs", aiHint: "CPU/processor model" },
      { key: "memory_storage", label: "Memory / Storage", type: "text", group: "Specs", aiHint: "RAM and storage specs" },
      { key: "display", label: "Display", type: "text", group: "Specs", aiHint: "screen size, resolution, type" },
      { key: "battery_life", label: "Battery Life", type: "text", group: "Power", aiHint: "battery duration, e.g. '10 hours'" },
      { key: "operating_system", label: "Operating System", type: "text", group: "Specs", aiHint: "OS name and version" },
    ],
    aiPromptContext: "Consumer electronics product. Include technical specifications, compatibility info, and key features.",
  },

  "clothing-apparel": {
    extraFields: [
      { key: "sleeve_length", label: "Sleeve Length", type: "select", options: ["sleeveless", "short", "3/4", "long"], group: "Sizing", aiHint: "sleeve length type" },
      { key: "neckline", label: "Neckline", type: "select", options: ["crew", "v_neck", "scoop", "collared", "turtleneck", "henley", "boat", "square"], group: "Details", aiHint: "neckline style" },
    ],
    aiPromptContext: "Clothing/apparel product. Include material composition, sizing info, and care instructions.",
  },

  "shoes-footwear": {
    extraFields: [
      { key: "shoe_width", label: "Width", type: "select", options: ["narrow", "standard", "wide", "extra_wide"], group: "Sizing", aiHint: "shoe width" },
      { key: "arch_type", label: "Arch Type", type: "select", options: ["flat", "neutral", "high"], group: "Sizing", aiHint: "recommended arch type" },
      { key: "sole_material", label: "Sole Material", type: "text", group: "Material & Care", aiHint: "outsole material, e.g. 'Rubber', 'EVA'" },
      { key: "heel_height", label: "Heel Height", type: "text", unit: "inches", group: "Details", aiHint: "heel height in inches" },
      { key: "waterproof", label: "Waterproof", type: "boolean", group: "Details", aiHint: "true or false" },
    ],
    aiPromptContext: "Footwear product. Include size systems (US/EU/UK), width, arch support, and material details.",
  },

  "home-garden": {
    extraFields: [
      { key: "room_type", label: "Room Type", type: "multi_select", options: ["living_room", "bedroom", "kitchen", "bathroom", "office", "outdoor", "garage"], group: "Usage", aiHint: "recommended room types" },
      { key: "assembly_required", label: "Assembly Required", type: "boolean", group: "Assembly", aiHint: "true or false" },
      { key: "assembly_time", label: "Assembly Time", type: "text", group: "Assembly", aiHint: "estimated assembly time, e.g. '30 minutes'" },
    ],
    aiPromptContext: "Home & garden product. Include dimensions, materials, and any assembly information.",
  },

  "furniture": {
    extraFields: [
      { key: "weight_capacity", label: "Weight Capacity", type: "text", group: "Specifications", aiHint: "maximum weight capacity with unit, e.g. '300 lbs'" },
      { key: "assembly_required", label: "Assembly Required", type: "boolean", group: "Assembly", aiHint: "true or false" },
      { key: "assembly_time", label: "Assembly Time", type: "text", group: "Assembly", aiHint: "estimated assembly time" },
      { key: "room_type", label: "Room Type", type: "multi_select", options: ["living_room", "bedroom", "dining_room", "office", "outdoor", "entryway", "nursery"], group: "Usage", aiHint: "recommended room types" },
      { key: "style", label: "Style", type: "select", options: ["modern", "traditional", "rustic", "mid_century", "industrial", "farmhouse", "coastal", "bohemian", "minimalist"], group: "Details", aiHint: "furniture style" },
    ],
    aiPromptContext: "Furniture product. Include dimensions, weight capacity, assembly info, and room recommendations.",
  },

  "beauty-personal-care": {
    extraFields: [
      { key: "skin_type", label: "Skin Type", type: "multi_select", options: ["normal", "dry", "oily", "combination", "sensitive", "all"], group: "Suitability", aiHint: "suitable skin types" },
      { key: "scent", label: "Scent", type: "text", group: "Details", aiHint: "fragrance description" },
      { key: "spf", label: "SPF", type: "number", group: "Details", aiHint: "sun protection factor, if applicable" },
      { key: "cruelty_free", label: "Cruelty Free", type: "boolean", group: "Ethics", aiHint: "true or false" },
      { key: "paraben_free", label: "Paraben Free", type: "boolean", group: "Ethics", aiHint: "true or false" },
    ],
    aiPromptContext: "Beauty/personal care product. Include ingredients, skin type suitability, and usage instructions.",
  },

  "health-wellness": {
    extraFields: [
      { key: "dosage", label: "Dosage", type: "text", group: "Usage", aiHint: "recommended dosage, e.g. '2 capsules daily'" },
      { key: "supplement_form", label: "Form", type: "select", options: ["capsule", "tablet", "softgel", "gummy", "powder", "liquid", "spray", "patch"], group: "Details", aiHint: "supplement form" },
      { key: "third_party_tested", label: "Third Party Tested", type: "boolean", group: "Quality", aiHint: "true or false" },
      { key: "warnings", label: "Warnings", type: "textarea", group: "Safety", aiHint: "health warnings and contraindications" },
    ],
    aiPromptContext: "Health/wellness product. Include supplement facts, dosage info, warnings, and certifications.",
  },

  "sports-outdoors": {
    extraFields: [
      { key: "activity_type", label: "Activity Type", type: "multi_select", options: ["running", "hiking", "cycling", "swimming", "yoga", "camping", "fishing", "climbing", "team_sports", "winter_sports"], group: "Activity", aiHint: "suitable activities" },
      { key: "skill_level", label: "Skill Level", type: "select", options: ["beginner", "intermediate", "advanced", "professional"], group: "Activity", aiHint: "recommended skill level" },
      { key: "weather_rating", label: "Weather Rating", type: "multi_select", options: ["all_weather", "warm", "cold", "rain", "snow", "wind", "uv_protection"], group: "Weather", aiHint: "weather suitability" },
    ],
    aiPromptContext: "Sports/outdoor product. Include activity type, sizing, weather rating, and performance specs.",
  },

  "toys-games": {
    extraFields: [
      { key: "age_range_min", label: "Age Range Min", type: "number", unit: "years", group: "Age & Safety", aiHint: "minimum recommended age" },
      { key: "age_range_max", label: "Age Range Max", type: "number", unit: "years", group: "Age & Safety", aiHint: "maximum recommended age, or 99 for no max" },
      { key: "player_count", label: "Player Count", type: "text", group: "Game Info", aiHint: "e.g. '2-4 players', '1+'" },
      { key: "batteries_required", label: "Batteries Required", type: "boolean", group: "Power", aiHint: "true or false" },
      { key: "battery_type", label: "Battery Type", type: "text", group: "Power", aiHint: "e.g. '2 AA', '3 AAA', 'rechargeable'" },
      { key: "piece_count", label: "Piece Count", type: "number", group: "Contents", aiHint: "number of pieces or parts" },
      { key: "safety_certifications", label: "Safety Certifications", type: "multi_select", options: ["ASTM", "CPSIA", "EN71", "CE"], group: "Age & Safety", aiHint: "safety certifications" },
    ],
    aiPromptContext: "Toy/game product. Include age range, safety warnings, battery info, and piece count.",
  },

  "books-media": {
    extraFields: [
      { key: "edition", label: "Edition", type: "text", group: "Details", aiHint: "edition number or name" },
      { key: "series", label: "Series", type: "text", group: "Details", aiHint: "series name if part of a series" },
    ],
    aiPromptContext: "Book/media product. Include author, ISBN, format, page count, and genre.",
  },

  "food-grocery": {
    extraFields: [
      { key: "calories_per_serving", label: "Calories per Serving", type: "number", group: "Nutrition", aiHint: "calories per serving" },
      { key: "net_weight", label: "Net Weight", type: "text", group: "Details", aiHint: "net weight with unit, e.g. '16 oz'" },
      { key: "origin_region", label: "Origin Region", type: "text", group: "Details", aiHint: "country or region of origin for the food" },
      { key: "expiration_type", label: "Expiration Type", type: "select", options: ["best_by", "use_by", "sell_by", "non_perishable"], group: "Storage", aiHint: "type of expiration dating" },
    ],
    aiPromptContext: "Food/grocery product. Include nutrition facts, allergens, ingredients, storage, and dietary info.",
  },

  "pet-supplies": {
    extraFields: [
      { key: "pet_type", label: "Pet Type", type: "multi_select", options: ["dog", "cat", "bird", "fish", "small_animal", "reptile", "horse"], group: "Pet Info", aiHint: "suitable pet types" },
      { key: "breed_size", label: "Breed Size", type: "multi_select", options: ["small", "medium", "large", "giant", "all"], group: "Pet Info", aiHint: "suitable breed sizes" },
      { key: "life_stage", label: "Life Stage", type: "multi_select", options: ["puppy_kitten", "adult", "senior", "all_stages"], group: "Pet Info", aiHint: "suitable life stages" },
      { key: "feeding_guide", label: "Feeding Guide", type: "textarea", group: "Usage", aiHint: "feeding instructions and recommended amounts" },
    ],
    aiPromptContext: "Pet supply product. Include pet type, breed size, life stage, ingredients, and feeding guide.",
  },

  "baby-kids": {
    extraFields: [
      { key: "age_range_min", label: "Age Range Min", type: "number", unit: "months", group: "Age & Safety", aiHint: "minimum age in months" },
      { key: "age_range_max", label: "Age Range Max", type: "number", unit: "months", group: "Age & Safety", aiHint: "maximum age in months" },
      { key: "safety_certifications", label: "Safety Certifications", type: "multi_select", options: ["CPSC", "ASTM", "JPMA", "Greenguard"], group: "Age & Safety", aiHint: "safety certifications" },
      { key: "bpa_free", label: "BPA Free", type: "boolean", group: "Safety", aiHint: "true or false" },
    ],
    aiPromptContext: "Baby/kids product. Include age range, safety certifications, materials, and care instructions.",
  },

  "automotive": {
    extraFields: [
      { key: "installation_difficulty", label: "Installation Difficulty", type: "select", options: ["diy_easy", "diy_moderate", "professional_recommended", "professional_required"], group: "Installation", aiHint: "installation difficulty level" },
      { key: "tools_required", label: "Tools Required", type: "text", group: "Installation", aiHint: "tools needed for installation" },
    ],
    aiPromptContext: "Automotive part. Include year/make/model fitment, OEM part number, specs, and installation info.",
  },

  "tools-hardware": {
    extraFields: [
      { key: "tool_type", label: "Tool Type", type: "select", options: ["hand_tool", "power_tool", "measuring", "cutting", "fastening", "plumbing", "electrical", "painting"], group: "Classification", aiHint: "type of tool" },
      { key: "torque", label: "Max Torque", type: "text", group: "Specs", aiHint: "maximum torque rating, e.g. '350 ft-lbs'" },
      { key: "speed", label: "Speed", type: "text", group: "Specs", aiHint: "speed rating, e.g. '2000 RPM'" },
      { key: "chuck_size", label: "Chuck / Blade Size", type: "text", group: "Specs", aiHint: "chuck, blade, or bit size" },
    ],
    aiPromptContext: "Tool/hardware product. Include power source, specs, compatibility, and safety information.",
  },

  "office-school": {
    extraFields: [
      { key: "pack_quantity", label: "Pack Quantity", type: "number", group: "Details", aiHint: "number of items in pack" },
      { key: "paper_size", label: "Paper Size", type: "select", options: ["letter", "legal", "A4", "A5", "tabloid", "custom"], group: "Specs", aiHint: "compatible paper size" },
      { key: "compatibility", label: "Compatibility", type: "text", group: "Specs", aiHint: "compatible devices or systems" },
    ],
    aiPromptContext: "Office/school supply. Include dimensions, compatibility, and pack quantity.",
  },

  "jewelry-watches": {
    extraFields: [
      { key: "metal_type", label: "Metal Type", type: "select", options: ["gold", "white_gold", "rose_gold", "silver", "sterling_silver", "platinum", "titanium", "stainless_steel", "brass", "copper"], group: "Materials", aiHint: "primary metal" },
      { key: "stone_type", label: "Stone Type", type: "text", group: "Materials", aiHint: "gemstone type, e.g. 'Diamond', 'Sapphire', 'Cubic Zirconia'" },
      { key: "carat_weight", label: "Carat Weight", type: "text", group: "Materials", aiHint: "total carat weight" },
      { key: "ring_size", label: "Ring Size", type: "text", group: "Sizing", aiHint: "ring size or size range" },
      { key: "chain_length", label: "Chain Length", type: "text", unit: "inches", group: "Sizing", aiHint: "chain length in inches" },
    ],
    aiPromptContext: "Jewelry/watch product. Include metal type, stone details, sizing, and certification info.",
  },

  "arts-crafts": {
    extraFields: [
      { key: "medium_type", label: "Medium", type: "multi_select", options: ["acrylic", "oil", "watercolor", "pencil", "charcoal", "pastel", "ink", "clay", "fabric", "paper", "wood", "resin"], group: "Medium", aiHint: "art medium types" },
      { key: "skill_level", label: "Skill Level", type: "select", options: ["beginner", "intermediate", "advanced", "all_levels"], group: "Details", aiHint: "recommended skill level" },
      { key: "pieces_included", label: "Pieces Included", type: "number", group: "Contents", aiHint: "number of pieces in kit" },
    ],
    aiPromptContext: "Arts & crafts product. Include medium type, skill level, and included materials.",
  },

  "musical-instruments": {
    extraFields: [
      { key: "instrument_type", label: "Instrument Type", type: "select", options: ["guitar", "bass", "drums", "keyboard", "piano", "violin", "saxophone", "trumpet", "flute", "ukulele", "dj_equipment", "recording"], group: "Classification", aiHint: "type of instrument" },
      { key: "skill_level", label: "Skill Level", type: "select", options: ["beginner", "intermediate", "advanced", "professional"], group: "Details", aiHint: "recommended skill level" },
      { key: "acoustic_electric", label: "Type", type: "select", options: ["acoustic", "electric", "acoustic_electric", "digital", "electronic"], group: "Details", aiHint: "acoustic, electric, or digital" },
      { key: "included_accessories", label: "Included Accessories", type: "text", group: "Contents", aiHint: "comma-separated list of included accessories" },
    ],
    aiPromptContext: "Musical instrument. Include instrument type, skill level, and included accessories.",
  },

  "collectibles": {
    extraFields: [
      { key: "condition_grade", label: "Condition Grade", type: "select", options: ["mint", "near_mint", "excellent", "very_good", "good", "fair", "poor"], group: "Condition", aiHint: "collectible condition grade" },
      { key: "year_era", label: "Year / Era", type: "text", group: "Provenance", aiHint: "year of production or era" },
      { key: "authenticity", label: "Authenticity", type: "select", options: ["certified", "authenticated", "unverified", "replica"], group: "Provenance", aiHint: "authenticity status" },
      { key: "edition", label: "Edition", type: "text", group: "Details", aiHint: "edition info, e.g. 'Limited Edition #234/500'" },
      { key: "provenance", label: "Provenance", type: "textarea", group: "Provenance", aiHint: "item history and ownership chain" },
    ],
    aiPromptContext: "Collectible/memorabilia. Include condition grading, authenticity, era, and provenance.",
  },

  "digital-products": {
    extraFields: [
      { key: "file_format", label: "File Format", type: "multi_select", options: ["PDF", "EPUB", "MP3", "MP4", "WAV", "PSD", "AI", "SVG", "ZIP", "EXE", "DMG"], group: "Format", aiHint: "file formats included" },
      { key: "file_size", label: "File Size", type: "text", group: "Format", aiHint: "total file size, e.g. '250 MB'" },
      { key: "license_type", label: "License Type", type: "select", options: ["personal", "commercial", "extended_commercial", "subscription", "lifetime"], group: "License", aiHint: "license type" },
      { key: "system_requirements", label: "System Requirements", type: "textarea", group: "Compatibility", aiHint: "OS, hardware, or software requirements" },
      { key: "instant_delivery", label: "Instant Delivery", type: "boolean", group: "Delivery", aiHint: "true for immediate download" },
    ],
    aiPromptContext: "Digital product. Include file format, size, license type, and system requirements.",
  },

  "services": {
    extraFields: [],
    aiPromptContext: "Service offering. Include duration, availability, booking requirements, and cancellation policy.",
  },

  "gift-cards": {
    extraFields: [
      { key: "delivery_method", label: "Delivery Method", type: "select", options: ["email", "physical", "both"], group: "Delivery", aiHint: "how the gift card is delivered" },
      { key: "expiry_months", label: "Expiry", type: "number", unit: "months", group: "Details", aiHint: "months until expiry, 0 for no expiry" },
      { key: "reloadable", label: "Reloadable", type: "boolean", group: "Details", aiHint: "true or false" },
    ],
    aiPromptContext: "Gift card product. Include delivery method and expiration details.",
  },

  "appliances": {
    extraFields: [
      { key: "energy_rating", label: "Energy Rating", type: "select", options: ["A+++", "A++", "A+", "A", "B", "C", "D"], group: "Energy", aiHint: "energy efficiency rating" },
      { key: "noise_level", label: "Noise Level", type: "text", unit: "dB", group: "Specs", aiHint: "noise level in decibels" },
      { key: "capacity", label: "Capacity", type: "text", group: "Specs", aiHint: "capacity with unit, e.g. '4.5 cu ft', '12 cups'" },
      { key: "installation_type", label: "Installation Type", type: "select", options: ["freestanding", "built_in", "countertop", "wall_mount", "under_counter"], group: "Installation", aiHint: "installation type" },
      { key: "finish", label: "Finish", type: "select", options: ["stainless_steel", "white", "black", "black_stainless", "slate", "custom_panel", "bisque"], group: "Appearance", aiHint: "appliance finish" },
    ],
    aiPromptContext: "Appliance product. Include energy rating, dimensions, capacity, and installation type.",
  },

  "outdoor-power": {
    extraFields: [
      { key: "engine_type", label: "Engine Type", type: "select", options: ["gas_2_stroke", "gas_4_stroke", "electric_corded", "electric_cordless", "battery", "manual"], group: "Engine", aiHint: "engine/motor type" },
      { key: "engine_cc", label: "Engine CC", type: "number", unit: "cc", group: "Engine", aiHint: "engine displacement in cc" },
      { key: "cutting_width", label: "Cutting Width", type: "text", group: "Specs", aiHint: "cutting width with unit, e.g. '21 inches'" },
      { key: "runtime", label: "Runtime", type: "text", group: "Specs", aiHint: "battery runtime per charge, e.g. '45 minutes'" },
      { key: "fuel_type", label: "Fuel Type", type: "select", options: ["gasoline", "diesel", "electric", "propane", "solar"], group: "Engine", aiHint: "fuel type" },
    ],
    aiPromptContext: "Outdoor power equipment. Include engine specs, fuel type, runtime, and cutting dimensions.",
  },

  "cell-phones": {
    extraFields: [
      { key: "screen_size", label: "Screen Size", type: "text", unit: "inches", group: "Display", aiHint: "screen diagonal in inches" },
      { key: "screen_resolution", label: "Screen Resolution", type: "text", group: "Display", aiHint: "e.g. '2556 x 1179'" },
      { key: "storage_options", label: "Storage Options", type: "text", group: "Specs", aiHint: "e.g. '128GB, 256GB, 512GB'" },
      { key: "ram", label: "RAM", type: "text", group: "Specs", aiHint: "RAM amount, e.g. '8GB'" },
      { key: "camera_specs", label: "Camera", type: "text", group: "Specs", aiHint: "camera resolution and features, e.g. '48MP + 12MP ultrawide'" },
      { key: "carrier_compatibility", label: "Carrier Compatibility", type: "multi_select", options: ["unlocked", "AT&T", "T-Mobile", "Verizon", "Sprint", "international"], group: "Compatibility", aiHint: "compatible carriers" },
      { key: "water_resistance", label: "Water Resistance", type: "text", group: "Durability", aiHint: "IP rating, e.g. 'IP68'" },
    ],
    aiPromptContext: "Cell phone/accessory. Include screen, processor, camera, storage, and carrier compatibility.",
  },

  "computers-tablets": {
    extraFields: [
      { key: "processor", label: "Processor", type: "text", group: "Specs", aiHint: "CPU model and speed" },
      { key: "ram", label: "RAM", type: "text", group: "Specs", aiHint: "RAM amount and type" },
      { key: "storage_type", label: "Storage", type: "text", group: "Specs", aiHint: "storage type and capacity, e.g. '512GB NVMe SSD'" },
      { key: "screen_size", label: "Screen Size", type: "text", unit: "inches", group: "Display", aiHint: "screen diagonal in inches" },
      { key: "screen_resolution", label: "Screen Resolution", type: "text", group: "Display", aiHint: "e.g. '2560 x 1600'" },
      { key: "graphics", label: "Graphics", type: "text", group: "Specs", aiHint: "GPU model" },
      { key: "operating_system", label: "Operating System", type: "text", group: "Specs", aiHint: "OS name and version" },
    ],
    aiPromptContext: "Computer/tablet. Include processor, RAM, storage, display, and OS details.",
  },

  "camera-photo": {
    extraFields: [
      { key: "megapixels", label: "Megapixels", type: "number", unit: "MP", group: "Sensor", aiHint: "effective megapixels" },
      { key: "sensor_size", label: "Sensor Size", type: "select", options: ["full_frame", "APS-C", "micro_four_thirds", "1_inch", "1/2.3_inch", "medium_format"], group: "Sensor", aiHint: "sensor format" },
      { key: "lens_mount", label: "Lens Mount", type: "text", group: "Compatibility", aiHint: "lens mount type, e.g. 'Canon RF', 'Sony E'" },
      { key: "video_resolution", label: "Video Resolution", type: "select", options: ["8K", "6K", "4K", "1080p", "720p"], group: "Video", aiHint: "maximum video resolution" },
      { key: "iso_range", label: "ISO Range", type: "text", group: "Sensor", aiHint: "native ISO range, e.g. '100-51200'" },
      { key: "stabilization", label: "Stabilization", type: "select", options: ["in_body", "lens_based", "electronic", "none"], group: "Features", aiHint: "image stabilization type" },
    ],
    aiPromptContext: "Camera/photo equipment. Include sensor, megapixels, video specs, and lens compatibility.",
  },

  "video-games": {
    extraFields: [
      { key: "platform", label: "Platform", type: "multi_select", options: ["PC", "PlayStation_5", "PlayStation_4", "Xbox_Series", "Xbox_One", "Nintendo_Switch", "Steam_Deck", "mobile"], group: "Platform", aiHint: "supported platforms" },
      { key: "esrb_rating", label: "ESRB Rating", type: "select", options: ["E", "E10+", "T", "M", "AO", "RP"], group: "Rating", aiHint: "ESRB content rating" },
      { key: "multiplayer", label: "Multiplayer", type: "select", options: ["single_player", "local_multiplayer", "online_multiplayer", "mmo", "co_op"], group: "Features", aiHint: "multiplayer mode" },
      { key: "game_genre", label: "Genre", type: "multi_select", options: ["action", "adventure", "rpg", "fps", "strategy", "simulation", "sports", "racing", "puzzle", "horror", "indie"], group: "Details", aiHint: "game genres" },
    ],
    aiPromptContext: "Video game/console. Include platform, rating, genre, and multiplayer support.",
  },

  "tv-home-audio": {
    extraFields: [
      { key: "screen_size", label: "Screen Size", type: "text", unit: "inches", group: "Display", aiHint: "screen diagonal in inches" },
      { key: "resolution", label: "Resolution", type: "select", options: ["8K", "4K", "1080p", "720p"], group: "Display", aiHint: "display resolution" },
      { key: "display_type", label: "Display Type", type: "select", options: ["OLED", "QLED", "LED", "Mini-LED", "LCD", "Plasma"], group: "Display", aiHint: "display technology" },
      { key: "smart_platform", label: "Smart Platform", type: "select", options: ["Google_TV", "Roku", "Fire_TV", "webOS", "Tizen", "Android_TV", "Apple_TV", "none"], group: "Smart Features", aiHint: "smart TV platform" },
      { key: "speaker_output", label: "Speaker Output", type: "text", unit: "W", group: "Audio", aiHint: "speaker wattage" },
      { key: "hdmi_ports", label: "HDMI Ports", type: "number", group: "Connectivity", aiHint: "number of HDMI ports" },
    ],
    aiPromptContext: "TV/home audio. Include screen size, resolution, display type, and connectivity details.",
  },

  "bags-luggage": {
    extraFields: [
      { key: "bag_capacity", label: "Capacity", type: "text", group: "Specifications", aiHint: "capacity in liters or volume" },
      { key: "laptop_size", label: "Laptop Compartment", type: "text", group: "Specifications", aiHint: "max laptop size, e.g. 'Up to 15.6 inches'" },
      { key: "tsa_approved", label: "TSA Approved", type: "boolean", group: "Travel", aiHint: "true or false" },
      { key: "wheels", label: "Wheels", type: "select", options: ["2_wheel", "4_wheel_spinner", "none"], group: "Features", aiHint: "wheel configuration" },
      { key: "water_resistant", label: "Water Resistant", type: "boolean", group: "Features", aiHint: "true or false" },
    ],
    aiPromptContext: "Bag/luggage product. Include dimensions, capacity, material, and travel features.",
  },

  "eyewear": {
    extraFields: [
      { key: "frame_shape", label: "Frame Shape", type: "select", options: ["rectangular", "round", "oval", "cat_eye", "aviator", "wayfarer", "sport", "butterfly", "square"], group: "Frame", aiHint: "frame shape" },
      { key: "frame_material", label: "Frame Material", type: "select", options: ["metal", "acetate", "titanium", "plastic", "nylon", "wood", "mixed"], group: "Frame", aiHint: "frame material" },
      { key: "lens_type", label: "Lens Type", type: "select", options: ["polarized", "mirrored", "photochromic", "gradient", "standard", "blue_light", "prescription_ready"], group: "Lens", aiHint: "lens type" },
      { key: "uv_protection", label: "UV Protection", type: "select", options: ["UV400", "100%_UVA_UVB", "partial", "none"], group: "Lens", aiHint: "UV protection level" },
      { key: "frame_width", label: "Frame Width", type: "text", unit: "mm", group: "Sizing", aiHint: "frame width in mm" },
    ],
    aiPromptContext: "Eyewear product. Include frame size, lens type, UV rating, and material details.",
  },

  "watches-accessories": {
    extraFields: [
      { key: "movement_type", label: "Movement", type: "select", options: ["automatic", "quartz", "mechanical", "solar", "kinetic", "digital", "smart"], group: "Movement", aiHint: "watch movement type" },
      { key: "water_resistance", label: "Water Resistance", type: "text", group: "Durability", aiHint: "water resistance rating, e.g. '100m / 10 ATM'" },
      { key: "case_diameter", label: "Case Diameter", type: "text", unit: "mm", group: "Sizing", aiHint: "case diameter in mm" },
      { key: "case_material", label: "Case Material", type: "text", group: "Materials", aiHint: "case material" },
      { key: "band_material", label: "Band Material", type: "select", options: ["leather", "stainless_steel", "silicone", "nylon", "ceramic", "titanium", "fabric", "rubber"], group: "Materials", aiHint: "band/strap material" },
      { key: "crystal_type", label: "Crystal", type: "select", options: ["sapphire", "mineral", "hardlex", "acrylic"], group: "Materials", aiHint: "crystal/glass type" },
    ],
    aiPromptContext: "Watch/accessory. Include movement type, water resistance, case size, and materials.",
  },

  "industrial-scientific": {
    extraFields: [
      { key: "accuracy", label: "Accuracy", type: "text", group: "Specs", aiHint: "measurement accuracy rating" },
      { key: "operating_temp_range", label: "Operating Temp Range", type: "text", group: "Specs", aiHint: "temperature range, e.g. '-20°C to 60°C'" },
      { key: "compliance", label: "Compliance", type: "multi_select", options: ["ISO_9001", "ISO_14001", "OSHA", "NIST", "ANSI", "IEC", "GMP"], group: "Compliance", aiHint: "regulatory compliance standards" },
      { key: "calibration", label: "Calibration", type: "select", options: ["factory_calibrated", "user_calibrated", "NIST_traceable", "not_applicable"], group: "Quality", aiHint: "calibration status" },
    ],
    aiPromptContext: "Industrial/scientific product. Include accuracy specs, compliance, and operating conditions.",
  },

  "lighting": {
    extraFields: [
      { key: "lumens", label: "Lumens", type: "number", group: "Light Output", aiHint: "brightness in lumens" },
      { key: "color_temp", label: "Color Temperature", type: "text", unit: "K", group: "Light Output", aiHint: "color temperature in Kelvin, e.g. '2700K (warm white)'" },
      { key: "bulb_type", label: "Bulb Type", type: "select", options: ["LED", "CFL", "incandescent", "halogen", "fluorescent", "smart_bulb"], group: "Bulb", aiHint: "bulb technology type" },
      { key: "fixture_type", label: "Fixture Type", type: "select", options: ["ceiling", "pendant", "floor_lamp", "table_lamp", "wall_sconce", "recessed", "track", "vanity", "outdoor", "landscape"], group: "Fixture", aiHint: "fixture/mounting type" },
      { key: "dimmable", label: "Dimmable", type: "boolean", group: "Features", aiHint: "true or false" },
      { key: "bulb_base", label: "Bulb Base", type: "select", options: ["E26", "E12", "GU10", "GU24", "G9", "MR16", "integrated"], group: "Bulb", aiHint: "bulb base type" },
    ],
    aiPromptContext: "Lighting product. Include lumens, color temperature, bulb type, and fixture style.",
  },

  "bedding-bath": {
    extraFields: [
      { key: "thread_count", label: "Thread Count", type: "number", group: "Quality", aiHint: "thread count for fabrics" },
      { key: "bed_size", label: "Bed Size", type: "select", options: ["twin", "twin_xl", "full", "queen", "king", "california_king", "crib", "toddler"], group: "Sizing", aiHint: "compatible bed size" },
      { key: "fabric_type", label: "Fabric Type", type: "select", options: ["cotton", "egyptian_cotton", "microfiber", "bamboo", "linen", "silk", "flannel", "percale", "sateen", "jersey"], group: "Material", aiHint: "fabric type" },
      { key: "hypoallergenic", label: "Hypoallergenic", type: "boolean", group: "Features", aiHint: "true or false" },
      { key: "piece_count", label: "Piece Count", type: "number", group: "Contents", aiHint: "number of pieces in set" },
    ],
    aiPromptContext: "Bedding/bath product. Include thread count, material, bed size, and care instructions.",
  },

  "kitchen-dining": {
    extraFields: [
      { key: "capacity_volume", label: "Capacity", type: "text", group: "Specifications", aiHint: "capacity with unit, e.g. '6 quarts', '12 cups'" },
      { key: "dishwasher_safe", label: "Dishwasher Safe", type: "boolean", group: "Care", aiHint: "true or false" },
      { key: "microwave_safe", label: "Microwave Safe", type: "boolean", group: "Care", aiHint: "true or false" },
      { key: "oven_safe_temp", label: "Oven Safe To", type: "text", group: "Care", aiHint: "max oven temp, e.g. '450°F'" },
      { key: "nonstick", label: "Nonstick", type: "boolean", group: "Features", aiHint: "true or false" },
      { key: "set_count", label: "Set Count", type: "number", group: "Contents", aiHint: "number of pieces in set" },
    ],
    aiPromptContext: "Kitchen/dining product. Include material, capacity, dimensions, and care instructions.",
  },

  "outdoor-patio": {
    extraFields: [
      { key: "weather_resistant", label: "Weather Resistant", type: "boolean", group: "Durability", aiHint: "true or false" },
      { key: "uv_resistant", label: "UV Resistant", type: "boolean", group: "Durability", aiHint: "true or false" },
      { key: "assembly_required", label: "Assembly Required", type: "boolean", group: "Assembly", aiHint: "true or false" },
      { key: "assembly_time", label: "Assembly Time", type: "text", group: "Assembly", aiHint: "estimated assembly time" },
      { key: "seating_capacity", label: "Seating Capacity", type: "number", group: "Specifications", aiHint: "number of seats" },
      { key: "cover_included", label: "Cover Included", type: "boolean", group: "Contents", aiHint: "true or false" },
    ],
    aiPromptContext: "Outdoor/patio product. Include weather resistance, dimensions, assembly, and material details.",
  },

  "general": {
    extraFields: [],
    aiPromptContext: "General product. Include all relevant specifications and details.",
  },
};

// ---------------------------------------------------------------------------
// Template-to-base-layout mapping (matches migration 033)
// ---------------------------------------------------------------------------

const TEMPLATE_BASE_LAYOUTS: Record<string, BaseLayout> = {
  "seeds-plants": "seed_plant",
  "electronics": "tech_specs",
  "clothing-apparel": "fashion",
  "shoes-footwear": "fashion",
  "home-garden": "generic",
  "furniture": "generic",
  "beauty-personal-care": "consumable",
  "health-wellness": "consumable",
  "sports-outdoors": "generic",
  "toys-games": "generic",
  "books-media": "media",
  "food-grocery": "consumable",
  "pet-supplies": "consumable",
  "baby-kids": "generic",
  "automotive": "vehicle_parts",
  "tools-hardware": "tech_specs",
  "office-school": "generic",
  "jewelry-watches": "fashion",
  "arts-crafts": "generic",
  "musical-instruments": "generic",
  "collectibles": "generic",
  "digital-products": "media",
  "services": "service",
  "gift-cards": "generic",
  "appliances": "tech_specs",
  "outdoor-power": "tech_specs",
  "cell-phones": "tech_specs",
  "computers-tablets": "tech_specs",
  "camera-photo": "tech_specs",
  "video-games": "media",
  "tv-home-audio": "tech_specs",
  "bags-luggage": "fashion",
  "eyewear": "fashion",
  "watches-accessories": "fashion",
  "industrial-scientific": "tech_specs",
  "lighting": "generic",
  "bedding-bath": "generic",
  "kitchen-dining": "generic",
  "outdoor-patio": "generic",
  "general": "generic",
};

// Template display names (matches migration 033)
const TEMPLATE_NAMES: Record<string, string> = {
  "seeds-plants": "Seeds & Plants",
  "electronics": "Electronics",
  "clothing-apparel": "Clothing & Apparel",
  "shoes-footwear": "Shoes & Footwear",
  "home-garden": "Home & Garden",
  "furniture": "Furniture",
  "beauty-personal-care": "Beauty & Personal Care",
  "health-wellness": "Health & Wellness",
  "sports-outdoors": "Sports & Outdoors",
  "toys-games": "Toys & Games",
  "books-media": "Books & Media",
  "food-grocery": "Food & Grocery",
  "pet-supplies": "Pet Supplies",
  "baby-kids": "Baby & Kids",
  "automotive": "Automotive",
  "tools-hardware": "Tools & Hardware",
  "office-school": "Office & School Supplies",
  "jewelry-watches": "Jewelry & Watches",
  "arts-crafts": "Arts & Crafts",
  "musical-instruments": "Musical Instruments",
  "collectibles": "Collectibles & Memorabilia",
  "digital-products": "Digital Products",
  "services": "Services",
  "gift-cards": "Gift Cards",
  "appliances": "Appliances",
  "outdoor-power": "Outdoor Power Equipment",
  "cell-phones": "Cell Phones & Accessories",
  "computers-tablets": "Computers & Tablets",
  "camera-photo": "Camera & Photo",
  "video-games": "Video Games & Consoles",
  "tv-home-audio": "TV & Home Audio",
  "bags-luggage": "Bags & Luggage",
  "eyewear": "Eyewear & Sunglasses",
  "watches-accessories": "Watches & Accessories",
  "industrial-scientific": "Industrial & Scientific",
  "lighting": "Lighting",
  "bedding-bath": "Bedding & Bath",
  "kitchen-dining": "Kitchen & Dining",
  "outdoor-patio": "Outdoor Living & Patio",
  "general": "General / Custom",
};

// ---------------------------------------------------------------------------
// Built template schemas (assembled from base + extras)
// ---------------------------------------------------------------------------

function buildSchema(slug: string): TemplateSchema {
  const baseLayout = TEMPLATE_BASE_LAYOUTS[slug];
  if (!baseLayout) throw new Error(`Unknown template slug: ${slug}`);

  const baseFields = BASE_LAYOUT_FIELDS[baseLayout];
  const extras = TEMPLATE_EXTRAS[slug] || { extraFields: [] };

  return {
    slug,
    name: TEMPLATE_NAMES[slug] || slug,
    baseLayout,
    fields: [...baseFields, ...extras.extraFields],
    aiPromptContext: extras.aiPromptContext,
  };
}

/** All 40 template schemas, keyed by slug */
export const TEMPLATE_SCHEMAS: Record<string, TemplateSchema> = Object.fromEntries(
  Object.keys(TEMPLATE_BASE_LAYOUTS).map((slug) => [slug, buildSchema(slug)])
);

/** All template slugs */
export const ALL_TEMPLATE_SLUGS = Object.keys(TEMPLATE_BASE_LAYOUTS);

/** All base layout types */
export const ALL_BASE_LAYOUTS: BaseLayout[] = [
  "seed_plant", "tech_specs", "fashion", "consumable",
  "vehicle_parts", "media", "service", "generic",
];

// ---------------------------------------------------------------------------
// Helper functions
// ---------------------------------------------------------------------------

/**
 * Get the full schema for a template slug.
 * Falls back to "general" if slug is unknown.
 */
export function getSchemaForTemplate(slug: string): TemplateSchema {
  return TEMPLATE_SCHEMAS[slug] || TEMPLATE_SCHEMAS["general"];
}

/**
 * Build a JSON schema description string for the AI prompt.
 * Returns an object mapping field keys to their AI hints.
 */
export function buildAiJsonSchema(slug: string): Record<string, string> {
  const schema = getSchemaForTemplate(slug);
  const result: Record<string, string> = {};

  for (const field of schema.fields) {
    const hint = field.aiHint || `${field.type} — ${field.label}`;
    const typeInfo = field.type === "multi_select"
      ? `array of strings`
      : field.type === "boolean"
        ? "boolean"
        : field.type === "number"
          ? `number${field.unit ? ` (${field.unit})` : ""}`
          : "string";
    result[field.key] = `${typeInfo} — ${hint}`;
  }

  return result;
}

/**
 * Get fields grouped by their group name (for form rendering).
 */
export function getFieldsByGroup(slug: string): Record<string, TemplateFieldDef[]> {
  const schema = getSchemaForTemplate(slug);
  const groups: Record<string, TemplateFieldDef[]> = {};

  for (const field of schema.fields) {
    if (!groups[field.group]) groups[field.group] = [];
    groups[field.group].push(field);
  }

  return groups;
}
